import React, { useState } from 'react';
import { Form, Input, Button, Card, message, Typography, Divider } from 'antd';
import { UserOutlined, LockOutlined, EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from "../../stores/authStore";
import api from '../../services/api';
import Logo from '../../components/common/Logo';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';

const { Title, Text } = Typography;

interface LoginForm {
  username: string;
  password: string;
}

const GOOGLE_CLIENT_ID = '681023234697-beqpuu6ivs8ggavreuila58v3s6r83lv.apps.googleusercontent.com'; // Thay bằng client id thực tế

const LoginPage: React.FC = () => {
  const { login } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const onFinish = async (values: LoginForm) => {
    try {
      setLoading(true);
      const response = await api.post('/auth/login', values);
      const { token, user } = response.data;
      
      login(user, token);
      message.success('Đăng nhập thành công!');
      navigate('/dashboard');
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Đăng nhập thất bại!');
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = () => {
    form.setFieldsValue({
      username: 'admin',
      password: 'password123'
    });
  };

  // Xử lý đăng nhập Google
  const handleGoogleLogin = async (credentialResponse: any) => {
    try {
      setLoading(true);
      const { credential } = credentialResponse;
      if (!credential) {
        message.error('Không nhận được token từ Google!');
        return;
      }
      // Gửi token Google về backend
      const response = await api.post('/auth/google-login', { token: credential });
      
      if (response.data.status === 'pending') {
        message.info('Tài khoản Google của bạn đã được đăng ký thành công! Vui lòng chờ admin kích hoạt tài khoản.');
      } else if (response.data.token && response.data.user) {
        login(response.data.user, response.data.token);
        message.success('Đăng nhập Google thành công!');
        navigate('/dashboard');
      } else {
        message.error(response.data.message || 'Đăng nhập Google thất bại!');
      }
    } catch (error: any) {
      console.error('Google login error:', error);
      message.error(error.response?.data?.message || 'Đăng nhập Google thất bại!');
    } finally {
      setLoading(false);
    }
  };

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <div style={{
        minHeight: '100vh',
        background: `url('/image/background.png')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'rgba(0, 0, 0, 0.3)',
          zIndex: 1
        }} />

        {/* Medicine Icons Background */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          zIndex: 2
        }}>
          {/* Pill Icons */}
          <div style={{
            position: 'absolute',
            top: '5%',
            left: '5%',
            fontSize: '24px',
            color: 'rgba(255,255,255,0.1)',
            animation: 'float 8s ease-in-out infinite'
          }}>
            💊
          </div>
          <div style={{
            position: 'absolute',
            top: '15%',
            right: '15%',
            fontSize: '32px',
            color: 'rgba(255,255,255,0.08)',
            animation: 'float 12s ease-in-out infinite'
          }}>
            🏥
          </div>
          <div style={{
            position: 'absolute',
            top: '25%',
            left: '20%',
            fontSize: '20px',
            color: 'rgba(255,255,255,0.12)',
            animation: 'float 10s ease-in-out infinite'
          }}>
            💉
          </div>
          <div style={{
            position: 'absolute',
            top: '35%',
            right: '25%',
            fontSize: '28px',
            color: 'rgba(255,255,255,0.09)',
            animation: 'float 15s ease-in-out infinite'
          }}>
            🩺
          </div>
          <div style={{
            position: 'absolute',
            top: '45%',
            left: '10%',
            fontSize: '22px',
            color: 'rgba(255,255,255,0.11)',
            animation: 'float 9s ease-in-out infinite'
          }}>
            💊
          </div>
          <div style={{
            position: 'absolute',
            top: '55%',
            right: '10%',
            fontSize: '26px',
            color: 'rgba(255,255,255,0.07)',
            animation: 'float 11s ease-in-out infinite'
          }}>
            🏥
          </div>
          <div style={{
            position: 'absolute',
            top: '65%',
            left: '30%',
            fontSize: '18px',
            color: 'rgba(255,255,255,0.13)',
            animation: 'float 13s ease-in-out infinite'
          }}>
            💉
          </div>
          <div style={{
            position: 'absolute',
            top: '75%',
            right: '20%',
            fontSize: '24px',
            color: 'rgba(255,255,255,0.1)',
            animation: 'float 7s ease-in-out infinite'
          }}>
            🩺
          </div>
          <div style={{
            position: 'absolute',
            top: '85%',
            left: '15%',
            fontSize: '30px',
            color: 'rgba(255,255,255,0.08)',
            animation: 'float 14s ease-in-out infinite'
          }}>
            💊
          </div>
          
          {/* Additional medicine icons in different positions */}
          <div style={{
            position: 'absolute',
            top: '10%',
            left: '60%',
            fontSize: '16px',
            color: 'rgba(255,255,255,0.06)',
            animation: 'float 16s ease-in-out infinite'
          }}>
            🏥
          </div>
          <div style={{
            position: 'absolute',
            top: '20%',
            right: '5%',
            fontSize: '20px',
            color: 'rgba(255,255,255,0.12)',
            animation: 'float 6s ease-in-out infinite'
          }}>
            💉
          </div>
          <div style={{
            position: 'absolute',
            top: '30%',
            left: '70%',
            fontSize: '26px',
            color: 'rgba(255,255,255,0.09)',
            animation: 'float 18s ease-in-out infinite'
          }}>
            🩺
          </div>
          <div style={{
            position: 'absolute',
            top: '40%',
            right: '35%',
            fontSize: '18px',
            color: 'rgba(255,255,255,0.11)',
            animation: 'float 8s ease-in-out infinite'
          }}>
            💊
          </div>
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '80%',
            fontSize: '22px',
            color: 'rgba(255,255,255,0.07)',
            animation: 'float 12s ease-in-out infinite'
          }}>
            🏥
          </div>
          <div style={{
            position: 'absolute',
            top: '60%',
            right: '45%',
            fontSize: '24px',
            color: 'rgba(255,255,255,0.1)',
            animation: 'float 9s ease-in-out infinite'
          }}>
            💉
          </div>
          <div style={{
            position: 'absolute',
            top: '70%',
            left: '50%',
            fontSize: '16px',
            color: 'rgba(255,255,255,0.13)',
            animation: 'float 15s ease-in-out infinite'
          }}>
            🩺
          </div>
          <div style={{
            position: 'absolute',
            top: '80%',
            right: '30%',
            fontSize: '28px',
            color: 'rgba(255,255,255,0.08)',
            animation: 'float 11s ease-in-out infinite'
          }}>
            💊
          </div>
          <div style={{
            position: 'absolute',
            top: '90%',
            left: '40%',
            fontSize: '20px',
            color: 'rgba(255,255,255,0.12)',
            animation: 'float 17s ease-in-out infinite'
          }}>
            🏥
          </div>
          
          {/* Small floating particles */}
          <div style={{
            position: 'absolute',
            top: '12%',
            left: '45%',
            fontSize: '12px',
            color: 'rgba(255,255,255,0.05)',
            animation: 'float 20s ease-in-out infinite'
          }}>
            💊
          </div>
          <div style={{
            position: 'absolute',
            top: '22%',
            left: '85%',
            fontSize: '14px',
            color: 'rgba(255,255,255,0.06)',
            animation: 'float 22s ease-in-out infinite'
          }}>
            💉
          </div>
          <div style={{
            position: 'absolute',
            top: '32%',
            left: '25%',
            fontSize: '10px',
            color: 'rgba(255,255,255,0.04)',
            animation: 'float 19s ease-in-out infinite'
          }}>
            🩺
          </div>
          <div style={{
            position: 'absolute',
            top: '42%',
            left: '90%',
            fontSize: '12px',
            color: 'rgba(255,255,255,0.07)',
            animation: 'float 21s ease-in-out infinite'
          }}>
            💊
          </div>
          <div style={{
            position: 'absolute',
            top: '52%',
            left: '5%',
            fontSize: '14px',
            color: 'rgba(255,255,255,0.05)',
            animation: 'float 23s ease-in-out infinite'
          }}>
            🏥
          </div>
          <div style={{
            position: 'absolute',
            top: '62%',
            left: '95%',
            fontSize: '10px',
            color: 'rgba(255,255,255,0.06)',
            animation: 'float 18s ease-in-out infinite'
          }}>
            💉
          </div>
          <div style={{
            position: 'absolute',
            top: '72%',
            left: '35%',
            fontSize: '12px',
            color: 'rgba(255,255,255,0.04)',
            animation: 'float 24s ease-in-out infinite'
          }}>
            🩺
          </div>
          <div style={{
            position: 'absolute',
            top: '82%',
            left: '75%',
            fontSize: '14px',
            color: 'rgba(255,255,255,0.05)',
            animation: 'float 20s ease-in-out infinite'
          }}>
            💊
          </div>
          <div style={{
            position: 'absolute',
            top: '92%',
            left: '20%',
            fontSize: '10px',
            color: 'rgba(255,255,255,0.06)',
            animation: 'float 22s ease-in-out infinite'
          }}>
            🏥
          </div>
        </div>

        <Card
          style={{
            width: '100%',
            maxWidth: '420px',
            borderRadius: '16px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
            border: 'none',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            position: 'relative',
            zIndex: 10
          }}
          bodyStyle={{
            padding: '40px 32px'
          }}
        >
          {/* Logo Section */}
          <div style={{ 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            marginBottom: '32px'
          }}>
            <Logo size="large" />
            <Title 
              level={2} 
              style={{ 
                marginTop: '16px', 
                marginBottom: '8px',
                color: '#000000',
                fontWeight: 700,
                textAlign: 'center',
                textShadow: '0 1px 2px rgba(0,0,0,0.1)'
              }}
            >
              Chào mừng trở lại
            </Title>
            <Text style={{ 
              color: '#333333', 
              fontSize: '15px',
              fontWeight: 500,
              textAlign: 'center'
            }}>
              Đăng nhập để tiếp tục quản lý hệ thống
            </Text>
          </div>

          <Form
            form={form}
            name="login"
            onFinish={onFinish}
            autoComplete="off"
            layout="vertical"
            size="large"
          >
            <Form.Item
              name="username"
              rules={[{ required: true, message: 'Vui lòng nhập tên đăng nhập!' }]}
            >
              <Input
                prefix={
                  <UserOutlined 
                    style={{ 
                      color: '#666666',
                      fontSize: '16px'
                    }} 
                  />
                }
                placeholder="Tên đăng nhập"
                style={{
                  height: '48px',
                  borderRadius: '8px',
                  border: '1px solid #d9d9d9',
                  fontSize: '15px',
                  fontWeight: 500
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#74b9ff';
                  e.target.style.boxShadow = '0 0 0 2px rgba(116, 185, 255, 0.2)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d9d9d9';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[{ required: true, message: 'Vui lòng nhập mật khẩu!' }]}
            >
              <Input.Password
                prefix={
                  <LockOutlined 
                    style={{ 
                      color: '#666666',
                      fontSize: '16px'
                    }} 
                  />
                }
                placeholder="Mật khẩu"
                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                style={{
                  height: '48px',
                  borderRadius: '8px',
                  border: '1px solid #d9d9d9',
                  fontSize: '15px',
                  fontWeight: 500
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#74b9ff';
                  e.target.style.boxShadow = '0 0 0 2px rgba(116, 185, 255, 0.2)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d9d9d9';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: '24px' }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                style={{
                  height: '48px',
                  borderRadius: '8px',
                  background: '#74b9ff',
                  border: 'none',
                  fontSize: '16px',
                  fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(116, 185, 255, 0.3)',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(116, 185, 255, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(116, 185, 255, 0.4)';
                }}
              >
                {loading ? 'Đang đăng nhập...' : 'Đăng nhập'}
              </Button>
            </Form.Item>
          </Form>

          {/* Nút đăng nhập Google */}
          <div style={{ margin: '16px 0', textAlign: 'center' }}>
            <GoogleLogin
              onSuccess={handleGoogleLogin}
              onError={() => message.error('Đăng nhập Google thất bại!')}
              width="100%"
              useOneTap
            />
          </div>
        </Card>

        <style>{`
          @keyframes float {
            0%, 100% { 
              transform: translateY(0px) translateX(0px) rotate(0deg); 
              opacity: 0.6;
            }
            25% { 
              transform: translateY(-15px) translateX(10px) rotate(90deg); 
              opacity: 0.8;
            }
            50% { 
              transform: translateY(-25px) translateX(-5px) rotate(180deg); 
              opacity: 1;
            }
            75% { 
              transform: translateY(-10px) translateX(-15px) rotate(270deg); 
              opacity: 0.7;
            }
          }
          
          @keyframes pulse {
            0%, 100% { transform: scale(1); opacity: 0.5; }
            50% { transform: scale(1.2); opacity: 0.9; }
          }
        `}</style>
      </div>
    </GoogleOAuthProvider>
  );
};

export default LoginPage; 